import type {
  MessengerConversation,
  MessengerMessage,
  MessengerPagedResult,
  MessengerParticipant,
  MessengerReadReceipt,
  MessengerUser
} from './types';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function unwrapApiValue(value: unknown): unknown {
  if (!isRecord(value)) return value;

  return value.data ?? value.value ?? value.result ?? value;
}

export function readStringValue(
  value: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'string' && item.trim()) {
      return item.trim();
    }

    if (typeof item === 'number' && Number.isFinite(item)) {
      return String(item);
    }
  }

  return undefined;
}

function readNumberValue(
  value: Record<string, unknown>,
  keys: string[]
): number | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'number' && Number.isFinite(item)) {
      return item;
    }

    if (typeof item === 'string') {
      const parsed = Number(item);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
}

function readBooleanValue(
  value: Record<string, unknown>,
  keys: string[]
): boolean | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'boolean') {
      return item;
    }
  }

  return undefined;
}

function readRecordValue(
  value: Record<string, unknown>,
  keys: string[]
): Record<string, unknown> | undefined {
  for (const key of keys) {
    const item = value[key];

    if (isRecord(item)) {
      return item;
    }
  }

  return undefined;
}

function readArrayValue(
  value: Record<string, unknown>,
  keys: string[]
): unknown[] {
  for (const key of keys) {
    const item = value[key];

    if (Array.isArray(item)) {
      return item;
    }
  }

  return [];
}

function normalizeDateValue(value?: string) {
  if (!value) {
    return new Date().toISOString();
  }

  const time = new Date(value).getTime();

  return Number.isNaN(time) ? new Date().toISOString() : value;
}

export function normalizeMessengerUser(value: unknown): MessengerUser | null {
  const record = isRecord(value) ? value : null;

  if (!record) {
    return null;
  }

  const id = readStringValue(record, [
    'id',
    'userId',
    'accountId',
    'accountUserId'
  ]);

  if (!id) {
    return null;
  }

  const firstName = readStringValue(record, ['firstName', 'givenName']);
  const lastName = readStringValue(record, ['lastName', 'surname']);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const displayName =
    readStringValue(record, [
      'displayName',
      'name',
      'fullName',
      'userName',
      'email'
    ]) ||
    fullName ||
    id;
  const roleNames = readArrayValue(record, ['roleNames', 'roles'])
    .map((role) =>
      typeof role === 'string'
        ? role
        : isRecord(role)
          ? readStringValue(role, ['name', 'roleName'])
          : undefined
    )
    .filter((role): role is string => Boolean(role));

  return {
    id,
    displayName,
    email: readStringValue(record, ['email', 'emailAddress']),
    avatarUrl: readStringValue(record, ['avatarUrl', 'imageUrl', 'imgUrl']),
    isActive: readBooleanValue(record, ['isActive', 'active']),
    tenantName: readStringValue(record, ['tenantName', 'companyName']),
    roleNames
  };
}

export function normalizeMessengerParticipant(
  value: unknown,
  currentUserId?: string
): MessengerParticipant | null {
  const record = isRecord(value) ? value : null;

  if (!record) {
    return null;
  }

  const nestedUser = readRecordValue(record, ['user', 'accountUser']);
  const source = nestedUser ?? record;
  const user = normalizeMessengerUser(source);
  const userId =
    user?.id ??
    readStringValue(record, [
      'userId',
      'participantUserId',
      'accountUserId',
      'id'
    ]);

  if (!userId) {
    return null;
  }

  return {
    userId,
    displayName:
      user?.displayName ??
      readStringValue(record, ['displayName', 'name', 'userName']) ??
      userId,
    email: user?.email ?? readStringValue(record, ['email', 'emailAddress']),
    avatarUrl:
      user?.avatarUrl ?? readStringValue(record, ['avatarUrl', 'imageUrl']),
    isCurrentUser:
      readBooleanValue(record, ['isCurrentUser', 'currentUser']) ??
      (currentUserId ? userId === currentUserId : undefined),
    readAt: readStringValue(record, ['readAt', 'lastReadAt']),
    lastReadMessageId: readStringValue(record, [
      'lastReadMessageId',
      'readMessageId'
    ])
  };
}

export function normalizeMessengerMessage(
  value: unknown,
  fallbackConversationId?: string,
  currentUserId?: string
): MessengerMessage | null {
  const wrapper = isRecord(value) ? value : null;
  const nestedMessage = wrapper
    ? readRecordValue(wrapper, ['message', 'chatMessage'])
    : undefined;
  const record = nestedMessage ?? wrapper;

  if (!record) {
    return null;
  }

  const conversationId =
    readStringValue(record, ['conversationId', 'chatConversationId']) ??
    fallbackConversationId ??
    readStringValue(wrapper ?? {}, ['conversationId']);
  const id =
    readStringValue(record, ['id', 'messageId']) ??
    readStringValue(record, ['clientMessageId']);
  const body =
    readStringValue(record, ['body', 'message', 'content', 'text']) ?? '';

  if (!conversationId || !id) {
    return null;
  }

  const senderUserId = readStringValue(record, [
    'senderUserId',
    'createdByUserId',
    'authorUserId',
    'userId'
  ]);
  const senderRecord = readRecordValue(record, ['sender', 'createdBy']);
  const sender = normalizeMessengerUser(senderRecord);

  return {
    id,
    conversationId,
    senderUserId: sender?.id ?? senderUserId,
    senderName:
      sender?.displayName ??
      readStringValue(record, ['senderName', 'authorName', 'createdByName']),
    senderAvatarUrl:
      sender?.avatarUrl ??
      readStringValue(record, [
        'senderAvatarUrl',
        'authorAvatarUrl',
        'createdByAvatarUrl',
        'avatarUrl',
        'imageUrl'
      ]),
    body,
    createdAt: normalizeDateValue(
      readStringValue(record, ['createdAt', 'sentAt', 'timestamp'])
    ),
    clientMessageId: readStringValue(record, ['clientMessageId']),
    isMine:
      readBooleanValue(record, ['isMine', 'mine', 'sentByCurrentUser']) ??
      (currentUserId && senderUserId ? currentUserId === senderUserId : false),
    status: 'sent'
  };
}

function normalizeLastMessage(
  record: Record<string, unknown>,
  conversationId: string,
  currentUserId?: string
): MessengerMessage | undefined {
  const lastMessageRecord = readRecordValue(record, [
    'lastMessage',
    'latestMessage'
  ]);

  if (lastMessageRecord) {
    return (
      normalizeMessengerMessage(
        lastMessageRecord,
        conversationId,
        currentUserId
      ) ?? undefined
    );
  }

  const body = readStringValue(record, ['lastMessageBody', 'lastMessageText']);
  const createdAt = readStringValue(record, ['lastMessageAt', 'updatedAt']);

  if (!body || !createdAt) {
    return undefined;
  }

  return {
    id: `last:${conversationId}:${createdAt}`,
    conversationId,
    body,
    createdAt: normalizeDateValue(createdAt),
    senderName: readStringValue(record, ['lastMessageSenderName']),
    senderUserId: readStringValue(record, ['lastMessageSenderUserId']),
    senderAvatarUrl: readStringValue(record, [
      'lastMessageSenderAvatarUrl',
      'senderAvatarUrl',
      'avatarUrl'
    ]),
    status: 'sent' as const
  };
}

export function normalizeMessengerConversation(
  value: unknown,
  currentUserId?: string
): MessengerConversation | null {
  const wrapper = isRecord(value) ? value : null;
  const nestedConversation = wrapper
    ? readRecordValue(wrapper, ['conversation', 'chatConversation'])
    : undefined;
  const record = nestedConversation ?? wrapper;

  if (!record) {
    return null;
  }

  const id = readStringValue(record, ['id', 'conversationId']);

  if (!id) {
    return null;
  }

  const participants = readArrayValue(record, [
    'participants',
    'users',
    'members'
  ])
    .map((participant) =>
      normalizeMessengerParticipant(participant, currentUserId)
    )
    .filter((participant): participant is MessengerParticipant =>
      Boolean(participant)
    );
  const lastMessage = normalizeLastMessage(record, id, currentUserId);
  const lastMessageAt =
    readStringValue(record, ['lastMessageAt']) ?? lastMessage?.createdAt;
  const updatedAt =
    readStringValue(record, ['updatedAt', 'modifiedAt']) ??
    lastMessageAt ??
    readStringValue(record, ['createdAt']);

  return {
    id,
    title: readStringValue(record, ['title', 'name', 'subject']),
    participants,
    lastMessage,
    lastMessageAt,
    updatedAt,
    createdAt: readStringValue(record, ['createdAt']),
    unreadCount:
      readNumberValue(record, [
        'unreadCount',
        'unreadMessagesCount',
        'notReadCount'
      ]) ?? 0,
    isDeleted: readBooleanValue(record, ['isDeleted', 'deleted'])
  };
}

export function normalizeMessengerReadReceipt(
  value: unknown
): MessengerReadReceipt | null {
  const record = isRecord(value) ? value : null;

  if (!record) {
    return null;
  }

  const conversationId = readStringValue(record, [
    'conversationId',
    'chatConversationId',
    'id'
  ]);

  if (!conversationId) {
    return null;
  }

  return {
    conversationId,
    userId: readStringValue(record, ['userId', 'participantUserId']),
    readAt: readStringValue(record, ['readAt', 'lastReadAt']),
    lastReadMessageId: readStringValue(record, [
      'lastReadMessageId',
      'readMessageId'
    ]),
    unreadCount: readNumberValue(record, ['unreadCount'])
  };
}

export function normalizePagedResult<T>(
  value: unknown,
  normalizer: (item: unknown) => T | null,
  fallbackPageNumber = 1,
  fallbackPageSize = 20
): MessengerPagedResult<T> {
  const payload = unwrapApiValue(value);
  const itemsValue = Array.isArray(payload)
    ? payload
    : isRecord(payload)
      ? readArrayValue(payload, ['items', 'data', 'results', 'rows'])
      : [];
  const record = isRecord(payload) ? payload : {};
  const items = itemsValue
    .map(normalizer)
    .filter((item): item is T => Boolean(item));
  const pageNumber =
    readNumberValue(record, ['pageNumber', 'page', 'currentPage']) ??
    fallbackPageNumber;
  const pageSize =
    readNumberValue(record, ['pageSize', 'size', 'limit']) ?? fallbackPageSize;
  const totalCount = readNumberValue(record, ['totalCount', 'total', 'count']);

  return {
    items,
    totalCount,
    pageNumber,
    pageSize,
    hasNextPage:
      readBooleanValue(record, ['hasNextPage', 'hasMore']) ??
      (totalCount !== undefined
        ? pageNumber * pageSize < totalCount
        : items.length >= pageSize)
  };
}
